import React from 'react';
import { VisualCalendar } from './VisualCalendar';
import { GuestCountPanel } from './GuestCountPanel';
import { FormSection, FormSectionHeader, FormSectionContent } from '../BookingFormLayout';
import type { DateGuestMessages, TentType } from '../types';
import type { RoomsPerDay } from '../../../state/reservation-store';

interface DateGuestStepProps {
  startDate: Date | null;
  endDate: Date | null;
  adults: number;
  children: number;
  maxCapacity: number;
  minDate: Date;
  maxDate: Date;
  availableDates: RoomsPerDay;
  rooms: [TentType, number][];
  onStartDateSelect: (date: Date) => void;
  onEndDateSelect: (date: Date) => void;
  onClearDates: () => void;
  onAdultsChange: (count: number) => void;
  onChildrenChange: (count: number) => void;
  bathingDates?: Date[];
  messages: DateGuestMessages;
}

/**
 * Date and guest selection step container.
 * Combines visual calendar with guest count panel.
 */
export const DateGuestStep: React.FC<DateGuestStepProps> = ({
  startDate,
  endDate,
  adults,
  children,
  maxCapacity,
  minDate,
  maxDate,
  availableDates,
  rooms,
  onStartDateSelect,
  onEndDateSelect,
  onClearDates,
  onAdultsChange,
  onChildrenChange,
  bathingDates,
  messages,
}) => {
  // Calculate nights for subtitle
  const nights =
    startDate && endDate
      ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      : 0;

  const subtitle =
    nights > 0
      ? `${nights} ${messages.nightsSelected}`
      : messages.selectDatesPrompt || messages.subtitle;

  return (
    <FormSection>
      <FormSectionHeader title={messages.title} subtitle={subtitle} />
      <FormSectionContent>
        <div className="space-y-8">
          {/* Calendar section */}
          <div>
            <VisualCalendar
              minDate={minDate}
              maxDate={maxDate}
              availableDates={availableDates}
              startDate={startDate}
              endDate={endDate}
              onStartDateSelect={onStartDateSelect}
              onEndDateSelect={onEndDateSelect}
              onClearDates={onClearDates}
              rooms={rooms}
              bathingDates={bathingDates}
              messages={{
                checkIn: messages.checkInLabel,
                checkOut: messages.checkOutLabel,
                nightsSelected: messages.nightsSelected,
                selectDates: messages.selectDatesPrompt,
                bathingDate: 'Bathing date',
                fullyBooked: 'Fully booked',
                clearDates: 'Clear dates',
              }}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Guest count section */}
          <div>
            <GuestCountPanel
              adults={adults}
              children={children}
              maxCapacity={maxCapacity}
              onAdultsChange={onAdultsChange}
              onChildrenChange={onChildrenChange}
              messages={{
                adultsLabel: messages.adultsLabel,
                childrenLabel: messages.childrenLabel,
                childrenDetail: messages.childrenDetail,
                guestsLabel: 'Number of guests',
                maxCapacityLabel: 'Room capacity',
              }}
            />
          </div>
        </div>
      </FormSectionContent>
    </FormSection>
  );
};

export default DateGuestStep;
